import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTAButtons } from "@/components/site/CTAButtons";
import { SITE } from "@/config/site";
import deepAssetUrl from "@/assets/svc-deep-new.webp";
const deepAsset = { url: deepAssetUrl };
import faceBgAssetUrl from "@/assets/izhub-face-bg.webp";
const faceBgAsset = { url: faceBgAssetUrl };

import tenancyAssetUrl from "@/assets/svc-tenancy.webp";
const tenancyAsset = { url: tenancyAssetUrl };
import airbnbAssetUrl from "@/assets/svc-airbnb.webp";
const airbnbAsset = { url: airbnbAssetUrl };
import militaryAssetUrl from "@/assets/svc-military.webp";
const militaryAsset = { url: militaryAssetUrl };
import office from "@/assets/service-office.webp";
import hero from "@/assets/hero-clean-living-room.webp";
const deep = deepAsset.url;
const tenancy = tenancyAsset.url;
const airbnb = airbnbAsset.url;
const military = militaryAsset.url;

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Cleaning Services: Domestic, Deep & End of Tenancy | IZHUB" },
      { name: "description", content: "Full range of cleaning services across Tidworth, Andover and Salisbury: domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning." },
      { property: "og:title", content: "Cleaning Services | IZHUB Cleaning" },
      { property: "og:description", content: "Domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning across Salisbury Plain." },
      { property: "og:url", content: "https://izhubcleaning.uk/services" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/services" }],
  }),
  component: Services,
});

type ServiceQuoteId = "regular" | "one-off" | "deep" | "tenancy" | "military" | "commercial" | "airbnb";
type Svc = {
  id: string;
  title: string;
  lede: string;
  bullets: string[];
  image: string;
  forWho: string;
  quoteService: ServiceQuoteId;
  detailTo?: "/services/domestic" | "/services/deep" | "/services/end-of-tenancy" | "/services/airbnb" | "/services/commercial" | "/services/military";
};

const svcs: Svc[] = [
  {
    id: "domestic",
    title: "Domestic cleaning",
    lede: "Reliable regular cleans that keep your home consistently fresh, weekly, fortnightly, or as often as you need.",
    forWho: "Busy households, shared homes, working professionals, families.",
    image: hero,
    quoteService: "regular",
    detailTo: "/services/domestic",
    bullets: ["Kitchens, bathrooms, living areas and bedrooms", "Dusting, vacuuming, mopping and surface cleans", "Weekly, fortnightly or one-off options", "Regular cleaner, consistent standards"],
  },
  {
    id: "deep",
    title: "Deep cleaning",
    lede: "A thorough top-to-bottom clean for homes that need more than a regular tidy, perfect as a reset or seasonal refresh.",
    forWho: "Homes that haven't been deep-cleaned recently, spring cleans, pre-viewing preparation.",
    image: deep,
    quoteService: "deep",
    detailTo: "/services/deep",
    bullets: ["Kitchen degrease including hob, oven exterior, extractor", "Bathroom limescale removal and grout attention", "Skirting boards, door frames, light switches, radiators", "Detailed surface, edge and often-missed areas"],
  },
  {
    id: "tenancy",
    title: "End of tenancy cleaning",
    lede: "Inspection-ready cleaning for tenants, landlords and letting agents preparing a property for checkout or new arrivals.",
    forWho: "Tenants moving out, landlords between lets, letting agents preparing inspections.",
    image: tenancy,
    quoteService: "tenancy",
    detailTo: "/services/end-of-tenancy",
    bullets: ["Full property clean to inspection standard", "Inside cupboards, drawers, oven interior and appliances", "Bathroom descale, limescale and mould treatment", "Optional add-ons: carpets, windows inside, upholstery"],
  },
  {
    id: "airbnb",
    title: "Airbnb & short-let cleaning",
    lede: "Fast, consistent turnarounds between guests, with an eye for the little details that earn 5★ reviews.",
    forWho: "Airbnb hosts, short-let owners, property managers.",
    image: airbnb,
    quoteService: "airbnb",
    detailTo: "/services/airbnb",
    bullets: ["Consistent turnaround windows around check-outs", "Fresh linen and towel changes (linen provided or ours)", "Restocking of essentials on request", "Discreet issue reporting with photos if needed"],
  },
  {
    id: "office",
    title: "Office cleaning",
    lede: "Reliable, discreet cleaning for small offices, workspaces and business premises, regular or as-needed.",
    forWho: "Small offices, coworking spaces, clinics, salons, studios.",
    image: office,
    quoteService: "commercial",
    detailTo: "/services/commercial",
    bullets: ["Desks, meeting rooms, kitchens and washrooms", "Waste, recycling and consumable restock", "Out-of-hours or in-hours to suit your team", "Regular scheduled visits or one-off deep cleans"],
  },
  {
    id: "military",
    title: "Military march-out cleaning",
    lede: "Specialist cleaning to the MOD inspection standard so you march out first time, without the stress.",
    forWho: "Serving personnel and military families in SFA across Tidworth, Bulford and Larkhill.",
    image: military,
    quoteService: "military",
    detailTo: "/services/military",
    bullets: ["Cleaned to march-out inspection standard", "Oven, hob, extractor, fridge/freezer inside and out", "Cupboard interiors, skirting, door frames, light fittings", "Bathroom descale, tiles, grout and sanitary fixtures"],
  },
];

function Services() {
  return (
    <>
      <PageHero
        title={<>Every kind of clean, <span className="rainbow-text">one trusted team</span>.</>}
        intro="From weekly household cleans to inspection-critical march-outs, we cover the full range of professional cleaning across Salisbury Plain."
        withBackground
        backgroundImage={faceBgAsset.url}
      >
        <CTAButtons />
      </PageHero>

      <section className="border-y border-border bg-background/85 backdrop-blur-sm overflow-hidden">
        <div className="container-x py-8 md:py-10">
          <p className="eyebrow justify-center flex">Our Cleaning Services</p>
          <div className="marquee mt-5" aria-label="Our cleaning services">
            <ul className="marquee-track list-none m-0 p-0" aria-hidden="false">
              {[...Array(2)].flatMap((_, dup) =>
                [
                  "Domestic cleaning",
                  "Deep cleaning",
                  "End of tenancy cleaning",
                  "Airbnb & short-let cleaning",
                  "Office cleaning",
                  "Military march-out cleaning",
                ].map((label) => (
                  <li
                    key={`${dup}-${label}`}
                    aria-hidden={dup === 1 ? "true" : undefined}
                    className="shrink-0 rounded-full border border-sage/40 bg-sage-soft/70 px-5 py-2 text-sm text-foreground/90"
                  >
                    {label}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </section>

      <div className="container-x py-20 md:py-24 flex flex-col gap-24">
        {svcs.map((s, i) => (
          <section key={s.id} id={s.id} className="scroll-mt-32 grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className={i % 2 === 1 ? "lg:order-2" : ""}>
              <img src={s.image} alt={s.title} loading="lazy" className="rounded-3xl w-full h-auto object-cover aspect-[4/3] shadow-soft" />
            </div>
            <div className="text-surface">
              <h2 className="font-display text-3xl md:text-4xl">{s.title}</h2>
              <p className="mt-4 text-lg text-muted-foreground">{s.lede}</p>
              <p className="mt-3 text-sm text-primary"><strong className="font-medium">Best for:</strong> {s.forWho}</p>
              <ul className="mt-6 space-y-2.5">
                {s.bullets.map((b) => (
                  <li key={b} className="flex gap-3 text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-sage" />{b}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/quote" search={{ service: s.quoteService }} className="btn-primary">Get my instant quote</Link>
                {s.detailTo && (
                  <Link to={s.detailTo} className="btn-outline">Learn more</Link>
                )}
                <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline">WhatsApp Us</a>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section className="pb-12">
        <div className="container-x">
          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <h2 className="font-display text-2xl md:text-3xl">Cleaning in Tidworth</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">Local pages with guide prices, covered neighbourhoods and what each clean includes.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/domestic-cleaning-tidworth" className="btn-outline">Domestic house cleaning in Tidworth</Link>
              <Link to="/office-cleaning-tidworth" className="btn-outline">Office &amp; commercial cleaning in Tidworth</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="rounded-3xl bg-sage-soft/60 border border-sage/30 p-8 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl mx-auto">Something else in mind?</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">If your job doesn't fit neatly into one of the above, get in touch and we'll help.</p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
          </div>
        </div>
      </section>
    </>
  );
}
